import { Dispatch, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencyEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { DividendPayerData, HighGrowthCompanyData } from '../../lib/models/fundamental/types';
import {
  getDividendPayers as getDividendPayersCompanies,
  getHighGrowthCompanies as getGrowthCompanies,
} from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { RootState } from '../../lib/store/index';
import { getCurrencySymbolByCurrencyCode } from '../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import NavButtons from '../../components/NavigationButtons/NavButtons';
import LongTermInvestments from '../../shared_components/LongTermInvestments/LongTermInvestments';
import {
  DividendPayerRow,
  HighGrowthCompanyRow,
  RenderTabsArgs,
} from '../../shared_components/LongTermInvestments/LongTermInvestments.types';
import { NavigationState } from '../../util/constants';
import { UI_ROUTES } from '../../util/routes';

const LongTermInvestmentsWrapper = (props: ReduxProps) => {
  const {
    highGrowthCompanies,
    dividendPayers,
    isDividendPayersStatusReady,
    isHighGrowthCompaniesStatusReady,
    companyLogoAndName,
    getDividendPayers,
    getHighGrowthCompanies,
  } = props;
  const [currentTab, setCurrentTab] = useState<number>(0);

  const navigate = useNavigate();

  const dividendsViewAllLink = {
    text: lang.commonViewAll(),
    link: UI_ROUTES.discover + UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath,
    state:
      dividendPayers.content.length && highGrowthCompanies.content.length
        ? { currentTab: null, currentTabIndex: currentTab }
        : { currentTab },
  };

  useEffect(() => {
    if (!highGrowthCompanies.content.length || !dividendPayers.content.length) {
      getDividendPayers();
      getHighGrowthCompanies();
    }
  }, [
    dividendPayers.content.length,
    getDividendPayers,
    getHighGrowthCompanies,
    highGrowthCompanies.content.length,
  ]);

  // Single market, and the response carries no currency: every row is priced in dollars.
  const dividendPayerRows = useMemo((): DividendPayerRow[] => (
    dividendPayers.content.map((item: DividendPayerData) => ({
      ...item,
      logoUrl: companyLogoAndName[item.symbol]?.url ?? item.url ?? '',
      currencySymbol: getCurrencySymbolByCurrencyCode(CurrencyEnum.USD),
    }))
  ), [dividendPayers.content, companyLogoAndName]);

  const highGrowthCompanyRows = useMemo((): HighGrowthCompanyRow[] => (
    highGrowthCompanies.content.map((item: HighGrowthCompanyData) => ({
      ...item,
      logoUrl: companyLogoAndName[item.symbol]?.url ?? item.url ?? '',
      currencySymbol: getCurrencySymbolByCurrencyCode(CurrencyEnum.USD),
    }))
  ), [highGrowthCompanies.content, companyLogoAndName]);

  const handleViewAllClick = () => {
    if (dividendsViewAllLink.state) {
      navigate(
        { pathname: dividendsViewAllLink.link },
        { state: { [NavigationState]: dividendsViewAllLink.state } },
      );
      return;
    }

    navigate(dividendsViewAllLink.link);
  };

  const handlebuttonViewAll = () => {
    if (!currentTab) {
      return dividendPayers.content.length > 0 ? dividendsViewAllLink : undefined;
    }
    if (currentTab) {
      return highGrowthCompanies.content.length > 0 ? dividendsViewAllLink : undefined;
    }
    return undefined;
  };

  const handleTabChange = (index: number) => {
    setCurrentTab(index);
  };

  const renderTabs = ({ tabs }: RenderTabsArgs) => (
    <NavButtons tabs={tabs} sendCurrentTabIndex={handleTabChange} />
  );

  const renderDesktopTabs = ({ tabs }: RenderTabsArgs) => (
    <NavButtons
      tabs={tabs}
      rightSideLink={handlebuttonViewAll()}
      sendCurrentTabIndex={handleTabChange}
    />
  );

  return (
    <LongTermInvestments
      dividendPayers={dividendPayerRows}
      highGrowthCompanies={highGrowthCompanyRows}
      hasTabs={
        isDividendPayersStatusReady
        && isHighGrowthCompaniesStatusReady
        && (dividendPayers.content.length > 0 || highGrowthCompanies.content.length > 0)
      }
      onViewAllClick={handleViewAllClick}
      renderTabs={renderTabs}
      renderDesktopTabs={renderDesktopTabs}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  highGrowthCompanies: state.fundamental.highGrowthCompanies,
  dividendPayers: state.fundamental.dividendPayers,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isDividendPayersStatusReady:
        isCallStatusReady(FundamentalCall.getDividendPayers, state.fundamental.statusByCall),
  isHighGrowthCompaniesStatusReady:
        isCallStatusReady(FundamentalCall.getHighGrowthCompanies, state.fundamental.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getHighGrowthCompanies: () => dispatch(getGrowthCompanies()),
  getDividendPayers: () => dispatch(getDividendPayersCompanies()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LongTermInvestmentsWrapper);
